import EchoService from "@/core/services/EchoService";
import {supportsCpsDetection} from "@/core/services/shellCapabilities";
import {useCpsStore} from "@/stores/cps.store";
import {CpsCardDetection, CpsLocatorReport} from "@/core/interfaces/CpsLocator";
import {AdacRelayAnswer, AdacRelayRequest} from "@/amelipro/interfaces";
import useCpsCode from "@/amelipro/composables/useCpsCode";
import useAmelipro from "@/amelipro/composables/useAmelipro";

const REQUEST_WHISPER = 'cps-locator.requested';
const REPORT_WHISPER = 'cps-locator.reported';
/** Asks the gateways alone to show themselves: the other posts stay silent, and their readers untouched. */
const GATEWAY_REQUEST_WHISPER = 'cps-locator.gateway-requested';
/** A workstation asking the gateway to act on its behalf, and the gateway's answer. */
const ADAC_REQUEST_WHISPER = 'adac.requested';
const ADAC_ANSWER_WHISPER = 'adac.answered';

/**
 * How long a workstation waits for the gateway's answer.
 *
 * The gateway may have to sign in again first — the portal drops its session
 * on its own — and that cycle alone is allowed a minute and a half.
 */
const RELAY_TIMEOUT = 150000;

/** How long a location round stays open before the screens stop expecting answers. */
const ROUND_DURATION = 20000;

/**
 * How many times a post asked to declare itself as a gateway reaches for its
 * reader when the reader does not conclude, and how long it waits between.
 */
const GATEWAY_PROBE_ATTEMPTS = 4;
const GATEWAY_PROBE_RETRY_MS = 3000;

/**
 * How long a conclusive detection is reused instead of powering the card up
 * again.
 *
 * Opening a CPS session is expensive — a minute on a workstation whose reader
 * answers slowly — and it holds the very lock the Vitale reads need, so the
 * patient detection is deaf for as long as the probe runs. The card is
 * therefore reached for as rarely as the map allows: never at boot, once per
 * window of time when the pharmacy asks, and on demand when a pharmacist
 * explicitly asks Connect to look again.
 */
const DETECTION_TTL = 10 * 60 * 1000;

type LocatorChannel = {
    whisper: (event: string, data: object) => unknown;
    listenForWhisper: <T>(event: string, callback: (data: T) => void) => LocatorChannel;
    subscribed: (callback: () => void) => LocatorChannel;
};

/*
 * Echo hands the same channel object back for a given name, so the state below
 * is shared by every caller of the composable inside a window: subscribing
 * twice would answer each request twice and power the card up twice for nothing.
 */
let channel: LocatorChannel | null = null;
let channelName: string | null = null;
/** Resolved once the channel is joined, never when its authorization fails. */
let ready: Promise<void> | null = null;
/** Whether this window answers the location requests of the pharmacy. */
let answering = false;
/** The probe in flight, shared so two callers never reach for the reader at once. */
let probing: Promise<CpsCardDetection> | null = null;
/** The last conclusive detection, and when the reader gave it. */
let detected: {detection: CpsCardDetection; at: number} | null = null;
/** The relay requests this window is waiting an answer to, by id. */
const pending = new Map<string, {resolve: (answer: AdacRelayAnswer | null) => void; timer: ReturnType<typeof setTimeout>}>();

/**
 * Keep the pharmacy's CPS map in this Connect's store.
 *
 * Every workstation announces its own reader on the pharmacy's private
 * channel, and every Connect stores every report that passes: each one then
 * knows which card is plugged where, and can send the pharmacist to the right
 * post without a scan. Nothing goes through the API — the exchange lives
 * entirely in whispers on that channel.
 */
const useCpsLocator = () => {
    const store = useCpsStore();

    /**
     * Ask the shell whether a CPS card sits in a reader on this workstation.
     */
    const read = async (): Promise<CpsCardDetection> => {
        if (!supportsCpsDetection()) {
            return {present: false, conclusive: false, error: 'cps-detection-unsupported'};
        }

        const detection = await window.ipcRenderer.detectCpsCard() as Partial<CpsCardDetection> | null;

        // The shell answers with an error of its own — a busy reader, a missing
        // driver — when it could not even ask: no presence to read there.
        if (typeof detection?.present !== 'boolean') {
            return {present: false, conclusive: false, error: detection?.error ?? 'cps-detection-failed'};
        }

        const read: CpsCardDetection = {
            present: detection.present,
            holder: detection.holder,
            civility: detection.civility,
            family: detection.family,
            title: detection.title,
            entity: detection.entity,
            validUntil: detection.validUntil,
            expired: detection.expired,
            test: detection.test,
            codeKept: detection.codeKept,
            gateway: detection.gateway,
            // Shells that predate the map do not say whether the reader
            // answered: an absence they report without an error came from one
            // that did.
            conclusive: detection.conclusive ?? (detection.present || detection.error === undefined),
            error: detection.error
        };

        // Only a conclusive answer is worth keeping: a reader that could not be
        // reached must be asked again, not remembered.
        if (read.conclusive) {
            detected = {detection: read, at: Date.now()};
        }

        return read;
    };

    /**
     * Probe this workstation's reader for a CPS card.
     *
     * The shell only powers the card up and releases it, so this stays safe
     * to run while the LGO holds its own reader session — but it is slow and
     * it takes the reader, so a recent answer is reused unless the caller
     * explicitly asks for a fresh one, which is what a pharmacist does when
     * they have just slid their card in.
     *
     * @param options Whether to reach for the reader even if a recent answer is known.
     */
    const probe = (options: {fresh?: boolean} = {}): Promise<CpsCardDetection> => {
        if (!options.fresh && detected && Date.now() - detected.at < DETECTION_TTL) {
            return Promise.resolve(detected.detection);
        }

        if (!probing) {
            probing = read().finally(() => {
                probing = null;
            });
        }

        return probing;
    };

    /**
     * Report this workstation's reader to the pharmacy, and to this client's
     * own store — a whisper never comes back to its sender.
     *
     * A probe that could not reach the reader says nothing at all: the other
     * workstations keep the card they knew about instead of being told it
     * vanished because the LGO held the reader for a second.
     *
     * @param hostName The host name of this workstation.
     * @param options  Whether to reach for the reader even if a recent answer is known.
     */
    const announce = async (hostName: string, options: {fresh?: boolean} = {}): Promise<void> => {
        const detection = await probe(options);

        if (!detection.conclusive) {
            return;
        }

        const report: CpsLocatorReport = {
            hostname: hostName,
            present: detection.present,
            holder: detection.holder,
            civility: detection.civility,
            family: detection.family,
            title: detection.title,
            entity: detection.entity,
            validUntil: detection.validUntil,
            gateway: detection.gateway === true
        };

        store.report(report);
        whisper(REPORT_WHISPER, report);
    };

    /**
     * Announce this workstation only if it may be a gateway.
     *
     * Reaching for the reader is what every other post is spared at boot; a
     * post that keeps a code porteur is the one whose card matters to the
     * whole pharmacy, and it is the one that speaks up unasked.
     *
     * @param hostName The host name of this workstation.
     */
    const announceIfGateway = async (hostName: string): Promise<void> => {
        if (!await useCpsCode().isKept()) {
            return;
        }

        // A post asked to show itself as a gateway may have just become one:
        // the configurator verified the code a moment ago, in the other
        // window, and the marker it wrote is not in the detection this window
        // cached ten minutes ago. So the reader is asked again — unless the
        // cache already says gateway, which does not go stale the other way
        // without the code changing, and that clears the cache's premise.
        //
        // And asked more than once when it does not conclude: a middleware
        // still waking up at boot, a reader held by the LGO for a second.
        // This is the one moment the post is invited to declare itself; an
        // unanswered invitation leaves the pharmacy without a gateway until
        // the next restart.
        for (let attempt = 1; attempt <= GATEWAY_PROBE_ATTEMPTS; attempt++) {
            const detection = await probe({fresh: attempt > 1 || detected?.detection.gateway !== true});

            if (detection.conclusive) {
                await announce(hostName);

                return;
            }

            await new Promise((resolve) => setTimeout(resolve, GATEWAY_PROBE_RETRY_MS));
        }
    };

    /**
     * Ask the gateways of the pharmacy — this post's own notification window
     * included — to announce themselves afresh.
     *
     * For the window that has just made this post a gateway: the configurator
     * verifies the code and saves, in the main window, while the reader and
     * the pharmacy map are owned by the notification window. A plain scan
     * request would be answered from that window's cache; this one is
     * answered from the reader.
     */
    const requestGateways = (): void => {
        whisper(GATEWAY_REQUEST_WHISPER, {});
    };

    /**
     * Look for the gateway again, from the reader: this window's own post,
     * fresh, and every other post asked to show itself.
     */
    const lookForGateway = async (): Promise<void> => {
        whisper(GATEWAY_REQUEST_WHISPER, {});

        if (answering && store.hostname) {
            await announce(store.hostname, {fresh: true});
        }
    };

    /**
     * Do on this workstation what another one asked its gateway to do, and
     * whisper the outcome back to it.
     *
     * @param request What was asked.
     * @param hostName The host name of this workstation.
     */
    const serve = async (request: AdacRelayRequest, hostName: string): Promise<void> => {
        if (request.to !== hostName || !request.id || !request.from) {
            return;
        }

        const amelipro = useAmelipro();
        const answer: AdacRelayAnswer = {id: request.id, from: hostName, to: request.from};

        try {
            if (request.kind === 'query') {
                const result = await amelipro.queryAdac(request.nir, request.slug);

                if ('verdict' in result) {
                    answer.verdict = result.verdict;
                } else {
                    // The gateway's own reason travels: the post that asked has
                    // no other way to learn that the card here refused the code.
                    answer.error = result.error;
                }

                answer.trace = result.trace;
            } else {
                // Nobody sits at a gateway to be handed the portal: background.
                const outcome = await amelipro.authenticateWithCps({background: true});

                answer.authenticated = outcome.authenticated;
                answer.step = outcome.step;
                answer.reason = outcome.reason;
                answer.trace = outcome.trace;
            }
        } catch {
            answer.error = 'adac:gateway-failed';
        }

        whisper(ADAC_ANSWER_WHISPER, answer);
    };

    /**
     * Ask the pharmacy's gateway to act for this workstation, and wait for
     * its answer.
     *
     * @param request What to ask, without the addressing.
     * @returns The gateway's answer, or null when there is no gateway or it never answered.
     */
    const askGateway = (
        request: {kind: 'connection'} | {kind: 'query'; nir: string; slug: string}
    ): Promise<AdacRelayAnswer | null> => {
        const gateway = store.remoteGateway;

        if (!gateway || !store.hostname || !channel) {
            return Promise.resolve(null);
        }

        const id = crypto.randomUUID();

        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                pending.delete(id);
                resolve(null);
            }, RELAY_TIMEOUT);

            pending.set(id, {resolve, timer});
            whisper(ADAC_REQUEST_WHISPER, {...request, id, from: store.hostname, to: gateway.hostname});
        });
    };

    /**
     * Hand an answer to whoever in this window is waiting for it.
     *
     * @param answer What a gateway whispered back.
     */
    const settle = (answer: AdacRelayAnswer): void => {
        if (answer?.to !== store.hostname) {
            return;
        }

        const waiting = pending.get(answer.id);

        if (!waiting) {
            return;
        }

        clearTimeout(waiting.timer);
        pending.delete(answer.id);
        waiting.resolve(answer);
    };

    /**
     * Say something to the other workstations, once the channel is joined.
     *
     * A client event sent before the subscription is confirmed is dropped on
     * the floor, and joining a private channel costs an authorization round
     * trip: the very first request would be lost without this.
     *
     * @param event The whisper name.
     * @param payload What to whisper.
     */
    const whisper = (event: string, payload: object): void => {
        ready?.then(() => channel?.whisper(event, payload));
    };

    /**
     * Join the pharmacy locator channel, on first use, and store every report
     * whispered on it.
     *
     * @param organizationId The organization of the pharmacy.
     */
    const join = (organizationId: number): LocatorChannel | null => {
        if (channel) {
            return channel;
        }

        const echo = EchoService.connect();

        if (!echo) {
            return null;
        }

        channelName = `connect.cps-locator.${organizationId}`;
        channel = echo.private(channelName) as unknown as LocatorChannel;
        channel.listenForWhisper<CpsLocatorReport>(REPORT_WHISPER, report => store.report(report));
        // Every window may ask the gateway; each one settles its own questions.
        channel.listenForWhisper<AdacRelayAnswer>(ADAC_ANSWER_WHISPER, settle);

        ready = new Promise(resolve => channel?.subscribed(resolve));

        return channel;
    };

    /**
     * Ask every workstation of the pharmacy to report its reader now.
     *
     * The answers land in the store as they arrive: the screens read the map,
     * they never wait for it. The round only tells them when to stop expecting
     * more.
     */
    const refresh = (): void => {
        if (!channel) {
            return;
        }

        store.beginScan();
        whisper(REQUEST_WHISPER, {});

        // This window's own request never comes back to it.
        if (answering && store.hostname) {
            announce(store.hostname);
        }

        // The round closes on the clock, never on the answers: a channel that
        // could not be joined must not leave the screens waiting forever.
        setTimeout(() => store.endScan(), ROUND_DURATION);
    };

    /**
     * Subscribe to the pharmacy locator channel and keep the store in step.
     *
     * Every window of every Connect listens, so each one holds the map. Only
     * the window that owns the reader — the notification one — answers the
     * requests: two windows probing the same workstation would fight over it.
     *
     * Subscribing asks nothing and probes nothing. Reaching for the card at
     * every boot would hold the reader for as long as it takes to answer, and
     * the pharmacy's first Vitale cards of the day are read in those seconds.
     *
     * @param organizationId The organization of the pharmacy.
     * @param hostName The host name of this workstation.
     * @param options Whether this window answers the location requests.
     */
    const subscribe = (
        organizationId: number | undefined,
        hostName: string | null,
        options: {answers?: boolean} = {}
    ): void => {
        if (hostName) {
            store.identify(hostName);
        }

        if (!organizationId || !join(organizationId)) {
            return;
        }

        if (options.answers && hostName && !answering) {
            answering = true;
            channel?.listenForWhisper(REQUEST_WHISPER, () => announce(hostName));
            channel?.listenForWhisper(GATEWAY_REQUEST_WHISPER, () => announceIfGateway(hostName));
            // The gateway does the pharmacy's ADAC calls: this is where they land.
            channel?.listenForWhisper<AdacRelayRequest>(ADAC_REQUEST_WHISPER, request => serve(request, hostName));

            // A gateway makes itself known as it comes up, and asks the ones
            // already up to do the same — so a counter never waits for a
            // scan to learn where its calls go. Nobody else touches a reader.
            announceIfGateway(hostName);
            whisper(GATEWAY_REQUEST_WHISPER, {});

            // Reverb drops every socket when it restarts — at each API release
            // — and pusher-js subscribes again once reconnected. What was
            // whispered in between reached no one: a counter that asked for
            // the gateway then would never learn where it is. So every
            // subscription after the first does what the start did.
            ready?.then(() => channel?.subscribed(() => {
                announceIfGateway(hostName);
                whisper(GATEWAY_REQUEST_WHISPER, {});
            }));
        }
    };

    /**
     * Leave the pharmacy locator channel.
     */
    const unsubscribe = (): void => {
        if (channelName) {
            EchoService.leave(channelName);
            channelName = null;
            channel = null;
            ready = null;
            answering = false;
        }
    };

    return {
        probe,
        subscribe,
        unsubscribe,
        refresh,
        requestGateways,
        lookForGateway,
        askGateway
    };
};

export default useCpsLocator;
